import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Users, FileText, AlertTriangle, Bell, ArrowUpRight, CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatName, fmtDate, fmtInt } from "@/lib/format";
import { TasksWidget } from "@/pages/TasksPage";
import DailyQuote from "@/components/DailyQuote";
import MarketSnapshot from "@/components/MarketSnapshot";
import PolicyRemindersBanner from "@/components/PolicyRemindersBanner";
import ReminderWidget from "@/components/ReminderWidget";
import AnimatedNumber from "@/components/AnimatedNumber";

function StatCard({ label, value, icon: Icon, accent, testId, onClick, staggerIndex = 0 }) {
  const Comp = onClick ? "button" : "div";
  const isNum = typeof value === "number";
  return (
    <Comp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`prime-card p-6 relative text-left w-full animate-fade-up stagger-${staggerIndex + 1} ${onClick ? "cursor-pointer" : ""}`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div className="label-uppercase text-muted-foreground">{label}</div>
        <Icon className="w-4 h-4 text-prime" strokeWidth={1.5} />
      </div>
      <div className={`font-display font-extrabold text-5xl tracking-tight mt-3 ${accent || ""}`}>
        {isNum ? <AnimatedNumber value={value} format={fmtInt} /> : value}
      </div>
    </Comp>
  );
}

const ROLE_LEGACY = { admin: "master", consultant: "basic" };

export default function DashboardPage() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = ROLE_LEGACY[user?.role] || user?.role || "basic";
  const [stats, setStats] = useState({ total_clients: 0, total_policies: 0, expiring_60d: 0, unread_notifications: 0 });
  const [expiring, setExpiring] = useState([]);
  const [clients, setClients] = useState({}); // id -> client

  useEffect(() => {
    if (role === "finanza") return;
    (async () => {
      try {
        const [s, e, c] = await Promise.all([
          api.get("/stats/dashboard"),
          api.get("/policies/expiring", { params: { days: 60 } }),
          api.get("/clients"),
        ]);
        setStats(s.data);
        setExpiring(e.data);
        const map = {};
        c.data.forEach(cl => map[cl.id] = cl);
        setClients(map);
      } catch { /* */ }
    })();
  }, [role]);

  // Finanzen role: land directly on the Finance dashboard.
  if (role === "finanza") return <Navigate to="/finance" replace />;

  const daysTo = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso); const now = new Date();
    return Math.ceil((d - now) / 86400000);
  };

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      <div className="flex items-end justify-between animate-fade-up">
        <div>
          <div className="label-uppercase text-prime">/ {t.dashboard}</div>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tight mt-1">Control Room</h1>
        </div>
      </div>

      {/* Policy expiry reminders banner */}
      <div className="animate-fade-up stagger-1"><PolicyRemindersBanner /></div>

      {/* Stat cards — quattro KPI in unica fila */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label={t.total_clients} value={stats.total_clients} icon={Users} testId="stat-clients" staggerIndex={1} onClick={() => navigate("/clients")} />
        <StatCard label={t.total_policies} value={stats.total_policies} icon={FileText} testId="stat-policies" staggerIndex={2} onClick={() => navigate("/policies")} />
        <StatCard label={t.expiring_soon} value={stats.expiring_60d} icon={AlertTriangle} accent="text-prime" testId="stat-expiring" staggerIndex={3} onClick={() => navigate("/policies?filter=expiring")} />
        <StatCard label={t.unread_notifs} value={stats.unread_notifications} icon={Bell} accent="text-foreground" testId="stat-unread" staggerIndex={4} onClick={() => navigate("/?notifs=open")} />
      </div>

      {/* ──── Doppia colonna: Aufgaben in alto, restanti widget compatti ──── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Colonna SX */}
        <div className="space-y-4">
          <div className="animate-fade-up stagger-5"><TasksWidget /></div>
          <div className="animate-fade-up stagger-6"><ReminderWidget days={180} /></div>
        </div>

        {/* Colonna DX */}
        <div className="space-y-4">
          {/* Expiring policies — versione compatta nella colonna destra */}
          <div className="prime-card animate-fade-up stagger-7" data-testid="expiring-table">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border">
              <div>
                <div className="label-uppercase text-muted-foreground text-[10px]">{t.expiring_soon}</div>
                <h2 className="font-display font-bold text-lg tracking-tight">Policen critiche</h2>
              </div>
              <Link to="/calendar">
                <Button variant="ghost" size="sm" className="rounded-none gap-1 text-[10px] label-uppercase h-7">
                  <CalendarClock className="w-3 h-3" /> {t.calendar}
                </Button>
              </Link>
            </div>
            {expiring.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">—</div>
            ) : (
              <div className="divide-y divide-border max-h-[440px] overflow-y-auto">
                {expiring.slice(0, 10).map(p => {
                  const d = daysTo(p.end_date);
                  const urgent = d <= 7;
                  const warning = !urgent && d <= 30;
                  const cl = clients[p.client_id];
                  const badgeClass = urgent
                    ? "bg-prime text-white"
                    : warning
                    ? "bg-amber-500 text-white"
                    : "bg-foreground text-background";
                  const tagLabel = urgent ? "URGENTE" : warning ? "30 G" : "60 G";
                  const tagColor = urgent ? "text-prime" : warning ? "text-amber-600" : "text-muted-foreground";
                  return (
                    <Link
                      to={`/clients/${p.client_id}`}
                      key={p.id}
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary transition-colors group"
                      data-testid={`expiring-row-${p.id}`}
                    >
                      <div className={`w-8 h-8 flex items-center justify-center font-display font-bold text-xs shrink-0 rounded-md ${badgeClass}`}>
                        {d}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-xs truncate">
                          {p.company} — <span className="text-muted-foreground">{t.insurance_types[p.insurance_type] || p.insurance_type}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground font-mono truncate">
                          {p.policy_number} · {cl ? `${formatName(cl.first_name)} ${formatName(cl.last_name)}` : "—"}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-[10px]">{fmtDate(p.end_date)}</div>
                        <div className={`label-uppercase text-[9px] ${tagColor}`}>{tagLabel}</div>
                      </div>
                      <ArrowUpRight className="w-3 h-3 text-muted-foreground group-hover:text-foreground" />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Märkte & Crypto — full width in fondo */}
      <MarketSnapshot />
    </div>
  );
}
